import { Injectable } from '@angular/core';
import { ValidatorFn } from '@angular/forms';
import { ProjectPermissions } from 'src/app/swagger-api';
import { MaybeUndefined } from 'src/app/utils';
import FieldsConstants from '../../common/fields.constants';
import { Attribute, ElementStyles, FieldAttribute, SelectorOption } from '../../interfaces/form-and-field.interface';
import FieldAllowedValueClasses from './field-allowed-value-classes.values';
import FieldAttributesValues from './field-attributes.values';

@Injectable()
export class FieldAttributeConstructorService extends FieldAttributesValues {
  public getFieldAttributes(
    backAttribute: Attribute,
    pageName: string,
    permissions?: ProjectPermissions,
  ): FieldAttribute {
    this.formateBackendAttribute(backAttribute);

    const fieldAttr: FieldAttribute = {
      elementDataTestId: `${pageName}-${backAttribute.name}-${backAttribute.scheme_name}-element`,
      errorDataTestId: `${pageName}-${backAttribute.name}-${backAttribute.scheme_name}-error`,
      id: `${backAttribute.name}-${backAttribute.scheme_name}`,
      hidden: false,
      backendAttribute: backAttribute,
      multiple: this.isMultiple(backAttribute.value_class_front),
      validators: [],
      elementStyles: {
        containerStyle: '',
        contentStyle: '',
      },
      disabled: permissions ? this.generateDisabledAtribute(permissions, backAttribute.name) : false,
    };

    if (this.isAllowedType(backAttribute.value_class_front)) {
      fieldAttr.elementStyles = this.formateElementStyle(backAttribute);
      fieldAttr.options = this.formateOptions(backAttribute);
      fieldAttr.validators = this.formateValidators(backAttribute);
    } else {
      fieldAttr.elementStyles = this.baseStyles;
      fieldAttr.options = [];
    }

    return fieldAttr;
  }

  public convertAttributesMetadata(
    attrs: Attribute[],
    pageName: string,
    permissions?: ProjectPermissions,
  ): FieldAttribute[] {
    return attrs.map((backend: Attribute) => this.getFieldAttributes(backend, pageName, permissions));
  }

  public formateValidators(attribute: Attribute): ValidatorFn[] {
    const types: MaybeUndefined<(attribute: Attribute) => ValidatorFn[]> =
      this.validatorsTypes[attribute.value_class_front];

    if (types) {
      return types(attribute);
    }

    return [];
  }

  private generateDisabledAtribute(permissions: ProjectPermissions, fieldName: string): boolean {
    if (fieldName === 'end_date' && permissions.extend) {
      return false;
    }
    return !permissions.update;
  }

  private formateBackendAttribute(attribute: Attribute): void {
    this.setupFieldLength(attribute);
  }

  private formateElementStyle(attribute: Attribute): ElementStyles {
    return this.styleClasses[attribute.value_class_front];
  }

  private formateOptions(attribute: Attribute): SelectorOption[] {
    return this.optionFieldTypes.includes(attribute.value_class_front)
      ? this.selectOptions[attribute.value_class_front]()
      : [];
  }

  private setupFieldLength(attributes: Attribute): void {
    if (
      !attributes.max_length ||
      !attributes.min_length ||
      attributes.max_length < 0 ||
      attributes.min_length < 0 ||
      attributes.max_length < attributes.min_length
    ) {
      attributes.max_length = FieldsConstants.MAX_BASE;
      attributes.min_length = FieldsConstants.MIN_BASE;
    }
  }

  private isAllowedType(type: string): boolean {
    return this.allowedTypes.includes(type);
  }

  private isMultiple(valueClassFront: string): boolean {
    return valueClassFront === FieldAllowedValueClasses.MULTIPLE_FILES;
  }
}
